(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (root) root.MarineMaxAIChatDealerPricingApi = api;
})(typeof window !== 'undefined' ? window : null, function () {
  'use strict';

  const pendingLeads = new Map();
  const sentLeads = new Map();
  const day = 24 * 60 * 60 * 1000;

  function apiError(status, detail) {
    const error = new Error(typeof detail === 'string' ? detail : 'The request could not be completed.');
    error.status = status;
    return error;
  }

  function errorKind(error) {
    const text = String(error?.message || '').toLowerCase();
    if (error?.status === 429) return 'rate-limit';
    if (text.includes('quote request limit')) return 'quote-limit';
    if (text.includes('maximum number of attempts')) return 'attempts';
    if (text.includes('verification code has expired')) return 'code-expired';
    if (text.includes('wrong email or verification code') || text.includes('wrong phone') || text.includes('wrong code')) return 'wrong-code';
    if (error?.status === 401) return 'token-expired';
    if (text.includes('valid price') || text.includes('valid msrp')) return 'pricing';
    return 'unavailable';
  }

  function createClient(options = {}) {
    const fetchImpl = options.fetch || globalThis.fetch?.bind(globalThis);
    const baseUrl = String(options.baseUrl || 'https://dev-api.inventoryiq.co').replace(/\/+$/, '');
    if (!/^https:\/\/[^/?#]+(?:\/[^?#]*)?$/.test(baseUrl)) throw new Error('A valid HTTPS dealer pricing API URL is required.');
    if (typeof fetchImpl !== 'function') throw new Error('Dealer pricing requires fetch.');

    async function post(path, payload, request = {}) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      if (request.signal?.aborted) abort();
      request.signal?.addEventListener('abort', abort, { once: true });
      const timeout = setTimeout(abort, options.timeoutMs || 60000);
      try {
        const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
        if (request.token) headers.Authorization = 'Bearer ' + request.token;
        const response = await fetchImpl(baseUrl + path, {
          method: 'POST', headers, body: JSON.stringify(payload), signal: controller.signal, credentials: 'omit',
        });
        let data;
        try { data = await response.json(); }
        catch (_) { throw apiError(response.status, 'The pricing service returned an unreadable response.'); }
        if (!response.ok) throw apiError(response.status, data?.detail);
        return data;
      } finally {
        clearTimeout(timeout);
        request.signal?.removeEventListener('abort', abort);
      }
    }

    function contact(channel, destination) {
      if (!['email', 'sms'].includes(channel) || !destination) throw new Error('A verification destination is required.');
      return channel === 'sms' ? { phone: destination } : { email: destination };
    }

    return Object.freeze({
      start: (channel, destination, signal) => post('/auth/passwordless/start', contact(channel, destination), { signal }),
      verify: (channel, destination, code, signal) => post('/auth/passwordless/token', { ...contact(channel, destination), code }, { signal }),
      unlock: (payload, token, signal) => post('/quotes/shopify/dealer-pricing', payload, { token, signal }),
      email: (quoteId, token, signal) => post('/quotes/shopify/dealer-pricing/email', { quote_id: quoteId }, { token, signal }),
    });
  }

  function toE164(phone, dialCode = '+1') {
    let digits = String(phone || '').replace(/\D/g, '');
    const prefix = String(dialCode).replace(/\D/g, '');
    if (prefix === '1' && digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
    if (prefix === '44') digits = digits.replace(/^0/, '');
    const result = '+' + prefix + digits;
    return /^\+[1-9]\d{7,14}$/.test(result) ? result : '';
  }

  function safeUrl(value) {
    try {
      const url = new URL(value);
      return url.protocol === 'https:' ? url.href : '';
    } catch (_) { return ''; }
  }

  function validQuote(quote, stockNumber) {
    const pricing = quote?.pricing;
    return Boolean(
      quote && quote.quote_id != null && String(quote.session_id || '').trim() &&
      String(quote.boat?.stock_number || '') === String(stockNumber) &&
      pricing && ['msrp', 'one_price', 'savings'].every((key) => typeof pricing[key] === 'number' && Number.isFinite(pricing[key])) &&
      pricing.msrp > 0 && pricing.one_price > 0 && pricing.msrp >= pricing.one_price && pricing.savings >= 0 &&
      /^[A-Z]{3}$/.test(pricing.currency || '') && /^\d{4}-\d{2}-\d{2}$/.test(pricing.valid_until || '') &&
      safeUrl(quote.pdf?.url) &&
      ['email', 'sms'].includes(quote.verified_channel?.type) && quote.verified_channel?.value
    );
  }

  function buildQuoteText(quote) {
    if (!String(quote?.boat?.stock_number || '').trim() || !validQuote(quote, quote.boat.stock_number)) throw new Error('The quote data required for the contact request is incomplete.');
    const text = (value) => typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
    const lines = [];
    const add = (label, value) => { const content = text(value); if (content) lines.push(label + ': ' + content); };
    const pricing = quote.pricing;
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: pricing.currency, minimumFractionDigits: 0, maximumFractionDigits: 2 });
    const date = new Date(pricing.valid_until + 'T12:00:00Z');
    if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== pricing.valid_until) throw new Error('The quote validity date is invalid.');
    const title = text(quote.boat.title);
    lines.push(title ? 'QUOTE - ' + title : 'QUOTE');
    add('Prepared for', [text(quote.user?.first_name), text(quote.user?.last_name)].filter(Boolean).join(' '));
    add('Stock', String(quote.boat.stock_number));
    add('Generated at', quote.generated_at);
    add('Currency', pricing.currency);
    add('MSRP', formatter.format(pricing.msrp));
    if (pricing.savings > 0) add('Savings', formatter.format(pricing.savings));
    add('One Price', formatter.format(pricing.one_price));
    add('Valid until', date.toLocaleDateString('en-US', { timeZone: 'UTC', month: 'long', day: 'numeric', year: 'numeric' }));
    const dealer = quote.dealer || {};
    add('Dealer', [text(dealer.name), text(dealer.state)].filter(Boolean).join(', '));
    add('Address', dealer.address);
    add('Phone', dealer.phone);
    add('Sales contact', dealer.sales_contact_name);
    add('Sales contact email', dealer.sales_contact_email);
    add('Location', safeUrl(dealer.location_url));
    add('Boat image', safeUrl(quote.boat.image_url));
    add('PDF', safeUrl(quote.pdf.url));
    return lines.join('\n');
  }

  function buildLeadPayload({ quote, values, context, consentText, consentLabel, pageUri, pageName, hutk }) {
    const config = context.hubspot || {};
    if (!values.consent || !consentText || !consentLabel) throw new Error('Consent is required.');
    if (!String(quote?.session_id || '').trim()) throw new Error('The quote session required for the contact request is missing.');
    if (![config.medium, config.channel, config.source].every((value) => String(value || '').trim())) throw new Error('HubSpot field values are not configured.');
    const verifiedChannel = quote?.verified_channel?.type;
    const contactMethod = verifiedChannel === 'email' ? 'Email' : verifiedChannel === 'sms' ? 'Text' : '';
    if (!contactMethod) throw new Error('A supported verified channel is required.');
    const entries = {
      firstname: values.firstName, lastname: values.lastName, email: values.email,
      phone: toE164(values.phone, values.dialCode), zip: values.zip,
      medium: config.medium, mmax_channel: config.channel, source: config.source,
      contactmethod: contactMethod,
      lastinterestedstocknumber: quote.boat.stock_number,
      triggertimelinenotes: buildQuoteText(quote),
      online_sales_quote_url: safeUrl(quote.pdf.url),
      boatbrand: context.boatBrand, boatmodel: context.boatModel, year: context.boatYear,
    };
    if (config.verifiedChannelField && config.verifiedChannelField !== 'contactmethod') entries[config.verifiedChannelField] = verifiedChannel;
    if (config.verifiedValueField) entries[config.verifiedValueField] = quote.verified_channel.value;
    if (config.sessionField) entries[config.sessionField] = quote.session_id;
    const leadContext = { pageUri, pageName };
    if (hutk) leadContext.hutk = hutk;
    return {
      fields: Object.entries(entries).filter(([, value]) => value != null && String(value).trim()).map(([name, value]) => ({ name, value: String(value) })),
      context: leadContext,
      legalConsentOptions: { consent: {
        consentToProcess: true, text: consentText,
        communications: [{ value: true, subscriptionTypeId: Number(config.subscriptionTypeId), text: consentLabel }],
      } },
    };
  }

  function validIpAddress(value) {
    if (typeof value !== 'string') return false;
    if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(value)) return value.split('.').every((part) => Number(part) <= 255);
    if (!value.includes(':') || !/^[a-f\d:.]+$/i.test(value)) return false;
    try { return new URL('https://[' + value + ']/').hostname.startsWith('['); }
    catch (_) { return false; }
  }

  async function resolveClientIp(options = {}) {
    const fetchImpl = options.fetch || globalThis.fetch?.bind(globalThis);
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (options.signal?.aborted) abort();
    options.signal?.addEventListener('abort', abort, { once: true });
    const timeout = setTimeout(abort, options.timeoutMs || 5000);
    try {
      // Same-origin CDN trace: no third-party lookup and no request on page load.
      const response = await fetchImpl('/cdn-cgi/trace', {
        method: 'GET', headers: { Accept: 'text/plain' }, credentials: 'omit',
        cache: 'no-store', redirect: 'error', signal: controller.signal,
      });
      if (!response.ok) throw apiError(response.status, 'The contact request could not be prepared.');
      const text = await response.text();
      const address = /^ip=([^\r\n]+)$/m.exec(text)?.[1]?.trim();
      if (!validIpAddress(address)) throw new Error('The contact request IP address is unavailable.');
      return address;
    } finally {
      clearTimeout(timeout);
      options.signal?.removeEventListener('abort', abort);
    }
  }

  function createLeadSender(options = {}) {
    const config = options.config || {};
    const fetchImpl = options.fetch || globalThis.fetch?.bind(globalThis);
    const storage = options.storage;
    const now = options.now || Date.now;
    const namespace = String(config.portalId) + ':' + String(config.formId);
    const storageKey = 'dealer-pricing:sent:' + namespace;

    function readSent() {
      let stored = {};
      try { stored = JSON.parse(storage?.getItem(storageKey) || '{}'); } catch (_) { stored = {}; }
      return Object.fromEntries(Object.entries(stored || {}).filter(([, timestamp]) => Number(timestamp) > now() - day));
    }

    async function submit(sessionId, payload) {
      const key = namespace + ':' + sessionId;
      const stored = readSent();
      if (sentLeads.get(key) > now() - day || stored[sessionId]) return { status: 'duplicate' };
      if (pendingLeads.has(key)) return pendingLeads.get(key);
      if (config.enabled !== true || !config.portalId || !config.formId) return { status: 'not-configured' };
      const send = (async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), options.timeoutMs || 15000);
        let response;
        try {
          const ipAddress = await resolveClientIp({ fetch: fetchImpl, signal: controller.signal });
          const submission = { ...payload, context: { ...payload.context, ipAddress } };
          response = await fetchImpl('https://api.hsforms.com/submissions/v3/integration/submit/' + encodeURIComponent(config.portalId) + '/' + encodeURIComponent(config.formId), {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(submission), credentials: 'omit', signal: controller.signal,
          });
        } finally { clearTimeout(timeout); }
        if (!response.ok) throw apiError(response.status, 'The contact request could not be submitted.');
        sentLeads.set(key, now());
        const updated = { ...readSent(), [sessionId]: now() };
        try { storage?.setItem(storageKey, JSON.stringify(Object.fromEntries(Object.entries(updated).slice(-100)))); } catch (_) { storage; }
        return { status: 'sent' };
      })();
      pendingLeads.set(key, send);
      try { return await send; }
      finally { pendingLeads.delete(key); }
    }

    return Object.freeze({ submit });
  }

  return Object.freeze({ createClient, errorKind, toE164, safeUrl, validQuote, buildQuoteText, buildLeadPayload, createLeadSender, resolveClientIp });
});
